import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Layers,
} from 'lucide-react';
import { NAVIGATION_ITEMS } from '../../constants/navigation';
import UserProfile from './UserProfile';

interface LayoutSidebarProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const LayoutSidebar: React.FC<LayoutSidebarProps> = ({
  sidebarCollapsed,
  setSidebarCollapsed,
}) => {
  const location = useLocation();

  return (
    <motion.aside
      className="glass border-r border-slate-200/60 dark:border-slate-800/60 flex-shrink-0 overflow-hidden"
      animate={{ width: sidebarCollapsed ? 72 : 280 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="flex flex-col h-full">
        {/* Brand Header with Glass Morphism */}
        <div className="px-6 py-6 border-b border-slate-200/60 dark:border-slate-700/60">
          <div className="flex items-center justify-between">
            <AnimatePresence mode="wait">
              {!sidebarCollapsed ? (
                <motion.div
                  key="expanded"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex items-center"
                >
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                    <Layers className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
                      DNS Fabric
                    </span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      Enterprise DNS
                    </p>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="collapsed"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center"
                >
                  <Layers className="w-6 h-6 text-white" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {!sidebarCollapsed && (
            <motion.button
              onClick={() => setSidebarCollapsed(true)}
              className="absolute top-6 right-4 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronLeft className="w-4 h-4" />
            </motion.button>
          )}

          {sidebarCollapsed && (
            <motion.button
              onClick={() => setSidebarCollapsed(false)}
              className="absolute top-6 right-4 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 scrollbar-thin overflow-y-auto">
          {NAVIGATION_ITEMS.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href === '/' && location.pathname === '/dashboard');
            const Icon = item.icon;

            return (
              <motion.div key={item.name} whileHover={{ x: 2 }}>
                <Link
                  to={item.href}
                  className={`${
                    isActive ? 'nav-item-active' : 'nav-item-inactive'
                  } group relative`}
                  title={sidebarCollapsed ? item.name : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        className="ml-3 overflow-hidden"
                      >
                        <span className="font-medium">{item.name}</span>
                        <p className="text-xs opacity-75 mt-0.5">{item.description}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute right-0 top-1/2 w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-l-full -translate-y-1/2"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              </motion.div>
            );
          })}
        </nav>

        {/* User Section */}
        <UserProfile sidebarCollapsed={sidebarCollapsed} />
      </div>
    </motion.aside>
  );
};

export default LayoutSidebar;